import { DeletedNote, NoteDiff } from './api';
import { EncryptedNote, RawNote } from './classes';

export const UUID_REGEX =
  /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/;

export const MIN_PASSWORD_LENGTH = 6;

// Endpoints
export type EndpointPayloads = {
  '/auth/signup': {
    payload: {
      username: string;
      password: string;
      notes: EncryptedNote[];
    };
    response: {
      access_token: string;
    };
  };
  '/auth/login': {
    payload: {
      username: string;
      password: string;
      notes: EncryptedNote[];
      deleted_notes: DeletedNote[];
    };
    response: {
      note_diff: NoteDiff;
      access_token: string;
    };
  };
  '/auth/logout': {
    payload: never;
    response: never;
  };
  '/notes/sync': {
    payload: {
      notes: EncryptedNote[];
      deleted_notes: DeletedNote[];
    };
    response: {
      note_diff: NoteDiff;
      access_token: string;
    };
  };
  '/account/delete': {
    payload: never;
    response: never;
  };
  '/account/change-password': {
    payload: {
      current_password: string;
      new_password: string;
      notes: EncryptedNote[];
    };
    response: {
      access_token: string;
    };
  };
};

export type Endpoint = keyof EndpointPayloads;

export const ENDPOINTS = [
  '/auth/signup',
  '/auth/login',
  '/auth/logout',
  '/notes/sync',
  '/account/delete',
  '/account/change-password',
] satisfies Endpoint[];

// Tauri commands
export type TauriCommandPayloads = {
  get_all_notes: {
    payload: never;
    response: RawNote[];
  };
  delete_notes: {
    payload: {
      notes: RawNote[];
    };
    response: never;
  };
  edit_note: {
    payload: {
      note: RawNote;
    };
    response: never;
  };
  sync_local_notes: {
    payload: {
      notes: RawNote[];
    };
    response: never;
  };
  import_notes: {
    payload: {
      paths: string[];
    };
    response: RawNote[];
  };
  export_notes: {
    payload: {
      notes: RawNote[];
      saveDir: string | string[];
    };
    response: never;
  };
  set_access_token: {
    payload: {
      username: string;
      accessToken: string;
    };
    response: never;
  };
  get_access_token: {
    payload: {
      username: string;
    };
    response?: string;
  };
  delete_access_token: {
    payload: {
      username: string;
    };
    response: never;
  };
  backup_notes: {
    payload: {
      notes: RawNote[];
      maxBackupsCount: number;
    };
    response: never;
  };
};

export type TauriCommand = keyof TauriCommandPayloads;

export const TAURI_COMMANDS = [
  'get_all_notes',
  'delete_notes',
  'edit_note',
  'sync_local_notes',
  'import_notes',
  'export_notes',
  'set_access_token',
  'get_access_token',
  'delete_access_token',
  'backup_notes',
] satisfies TauriCommand[];

// Tauri emits
export const TAURI_EMITS = ['auth'] as const;

export type TauriEmit = (typeof TAURI_EMITS)[number];

// Tauri listeners
export const TAURI_LISTENERS = [
  'reload',
  'new-note',
  'delete-selected-notes',
  'import-notes',
  'export-note',
  'export-all-notes',
  'login',
  'logout',
  'signup',
  'change-password',
  'delete-account',
] as const;

export type TauriListener = (typeof TAURI_LISTENERS)[number];

// Update strategies
export const UPDATE_STRATEGIES = ['auto', 'manual'] as const;

export type UpdateStrategy = (typeof UPDATE_STRATEGIES)[number];

// Colour themes
export const COLOUR_THEMES = ['Light', 'Dark', 'System'] as const;

export type ColourTheme = (typeof COLOUR_THEMES)[number];

// Media queries
// Note: This needs to be kept in sync with the $small-screen-width Sass var
export const MEDIA_QUERY_SMALL_SCREEN = '(max-width: 650px)';
export const MEDIA_QUERY_TOUCH_DEVICE = '(pointer: coarse)';

// Long press timeout (ms)
export const LONG_PRESS_TIMEOUT = 500;

// Menu dimensions
export const MIN_MENU_WIDTH = 150;

// Backups
export const MAX_BACKUPS_COUNT = 3;
